import type { CreateTenantInfoDto, TenantInfoDto, TenantInfoListDto, UpdateTenantInfoDto } from './common/models';
import type { DataRequestDto, EntitySchemaDto, IdNameDto, PageRequestDto } from './models';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TenantInfoService {
  private restService = inject(RestService);
  apiName = 'Default';
  

  create = (input: CreateTenantInfoDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TenantInfoDto>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createTenantUserProfile = (tenantId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/tenant-info/tenant-user-profile/${tenantId}`,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/tenant-info/${id}`,
    },
    { apiName: this.apiName,...config });
  

  get = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TenantInfoDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/tenant-info/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getAvailableTenants = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, TenantInfoListDto[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info/available-tenants',
    },
    { apiName: this.apiName,...config });
  

  getCount = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info/count',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getDropDownOptions = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<IdNameDto>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info/drop-down-options',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getExpandById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TenantInfoDto[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/tenant-info/${id}/expand`,
    },
    { apiName: this.apiName,...config });
  

  getExtraPropertiesById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Record<string, object>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/tenant-info/${id}/extra-properties`,
    },
    { apiName: this.apiName,...config });
  

  getFullListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TenantInfoListDto[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info/full-list-with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getIds = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info/ids',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getList = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<TenantInfoDto>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<TenantInfoListDto>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info/with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getOneExtraPropertiesByIdAndKey = (id: string, key: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, object>({
      method: 'GET',
      url: `/api/app/tenant-info/${id}/one-extra-properties`,
      params: { key },
    },
    { apiName: this.apiName,...config });
  

  getSchema = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, EntitySchemaDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info/schema',
    },
    { apiName: this.apiName,...config });
  

  getTenantByName = (name: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TenantInfoDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info/tenant-by-name',
      params: { name },
    },
    { apiName: this.apiName,...config });
  

  getTenantName = (domain: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TenantInfoDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/tenant-info/tenant-name',
      params: { domain },
    },
    { apiName: this.apiName,...config });
  

  getWithDetails = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TenantInfoDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/tenant-info/${id}/with-details`,
    },
    { apiName: this.apiName,...config });
  

  setExtraPropertiesByIdAndKeyAndValue = (id: string, key: string, value: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/tenant-info/${id}/set-extra-properties`,
      params: { key, value },
    },
    { apiName: this.apiName,...config });
  

  update = (id: string, input: UpdateTenantInfoDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TenantInfoDto>({
      method: 'PUT',
      headers: { Accept: 'application/json' },
      url: `/api/app/tenant-info/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
}